import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../utils/Auth/AuthProvider';
import { useAddSubChapter, useAddSubjectChapter, useSchoolGradeSubjects, useSchoolGrades, useSubjectChapters, useUserInfo } from '../../react-query/queries';
import SubchapterDialog from '../../components/SchoolSetup/SubchapterDialog';
import { Button, EmptyState, FormField, KpiCard, Modal, PageHeader, PagedFooter, PanelCard, PillTabs, Segmented, Spinner, Switch, TextLink, Track } from '../../school_panel/components/ui';
import { LINK_BTN } from '../../school_panel/components/ui/linkButton';
import { setupStepTotal } from '../../school_panel/components/ui/SetupSteps';
import { gradeLabel, gradeCompact } from '../../school_panel/lib/gradeLabel';
import { FaBookOpen, FaChevronRight, FaPlus } from '../../school_panel/components/ui/icons';
import { ArrowRight, ListFilter } from 'lucide-react';
import { useClassPerfChapterMastery } from '../../school_panel/api/hooks';
import { RemoveDialog, RemoveTarget } from './blueprint';
import usePagination from '../../school_panel/lib/usePagination';
import { NAME_MAX, fieldClass } from '../../school_panel/lib/fieldClass';
import { adminGetInstitutionFields, adminGetFieldAcademicYears, adminGetAcademicYearCourses, AdminField, AdminAcademicYear } from '../../utils/remote';

// A URL id is only used when it is a whole number.
const LABEL = 'font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted';

const idParam = (raw: string | null): number | null => {
  if (raw == null || !/^\d+$/.test(raw)) return null;
  return Number(raw);
};

const ChapterSetup = () => {
  const [searchParams] = useSearchParams();
  const { token, role } = useAuth();
  const isAllowed = !!token && (role === 'superuser' || role === 'school' || role === 'school_admin' || role === 'admin' || role === 'college_university');

  const { data: gradesData } = useSchoolGrades(token);
  // The classes page links here with ?gradeId=&subjectId= to preselect.
  const [gradeId, setGradeId] = useState<number | null>(() => idParam(searchParams.get('gradeId')));
  const { data: subsData } = useSchoolGradeSubjects(token, gradeId ? String(gradeId) : '0');
  const [subjectId, setSubjectId] = useState<number | null>(() => idParam(searchParams.get('subjectId')));
  // University flow states
  const [fieldsData, setFieldsData] = useState<AdminField[] | null>(null);
  const [academicYears, setAcademicYears] = useState<AdminAcademicYear[] | null>(null);
  const [academicYearId, setAcademicYearId] = useState<number | null>(null);
  const [coursesData, setCoursesData] = useState<{ id: number; name: string }[] | null>(null);
  const { data: userInfo } = useUserInfo();

  // Determine if user's institution is university/college (used to switch flows)
  const isUniversity = (() => {
    const instType = (userInfo?.institution_type || (role === 'college_university' ? 'University/College' : ''));
    const t = String(instType || '').toLowerCase();
    return t.includes('university') || t.includes('college') || role === 'college_university';
  })();

  // Only use the subject-chapters hook for school flow. For university flow we fetch course chapters separately.
  const { data: chaptersData, refetch: refetchChaps, isFetching } = useSubjectChapters(token, isUniversity ? null : subjectId, isUniversity ? null : gradeId);
  const addChapter = useAddSubjectChapter();
  const addSubchapter = useAddSubChapter();
  const [inlineSub, setInlineSub] = useState('');
  const [inlineSubError, setInlineSubError] = useState<string | null>(null);
  const [newChapter, setNewChapter] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isPresetModalOpen, setIsPresetModalOpen] = useState(false);
  const [presetChapters, setPresetChapters] = useState<any[]>([]);
  const [selectedPresetIds, setSelectedPresetIds] = useState<number[]>([]);
  const [courseChapters, setCourseChapters] = useState<any[] | null>(null);
  const [isUploadingJson, setIsUploadingJson] = useState(false);
  const [useChapterOption, setUseChapterOption] = useState(false);

  const [isSubchapterModalOpen, setIsSubchapterModalOpen] = useState(false);
  const [selectedChapterId, setSelectedChapterId] = useState<number | null>(null);
  const [selectedChapterName, setSelectedChapterName] = useState('');
  const [expandedChapterId, setExpandedChapterId] = useState<number | null>(null);
  const [removeTarget, setRemoveTarget] = useState<RemoveTarget | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleJsonFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (subjectId == null) {
      alert('Please select a course before uploading JSON');
      e.currentTarget.value = '';
      return;
    }
    setIsUploadingJson(true);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const remote = await import('../../utils/remote');
      if (useChapterOption) {
        await remote.uploadCourseChapterOptionsFromJSON(token, subjectId, parsed);
        // refresh chapter options
        const res = await remote.getCourseChapterOptions(token, subjectId);
        setCourseChapters(res.chapters || []);
      } else {
        await remote.uploadCourseChaptersFromJSON(token, subjectId, parsed);
        // refresh course chapters
        const res = await remote.getCourseChapters(token, subjectId);
        setCourseChapters(res.chapters || []);
      }
      // optional: also refresh legacy chapters hook
      refetchChaps();
    } catch (err) {
      
      alert('Failed to upload JSON. See console for details.');
    } finally {
      setIsUploadingJson(false);
      e.currentTarget.value = '';
    }
  };

  useEffect(() => {
    // Only auto-select a grade for school flow; for university flow we'll load departments separately
    if (!isUniversity && gradesData?.grades?.length && gradeId == null) setGradeId(gradesData.grades[0].id);
  }, [gradesData, gradeId]);

  useEffect(() => {
    // Only auto-select a subject for school flow
    if (!isUniversity && subsData?.subjects?.length && subjectId == null) setSubjectId(subsData.subjects[0].id);
  }, [subsData, subjectId]);

  useEffect(() => {
    // Only trigger subject-chapters refetch for school flow
    if (!isUniversity && subjectId != null) refetchChaps();
  }, [isUniversity, subjectId, refetchChaps, useChapterOption]);

  

  // For university users, load chapters by course (since subjectId represents course)
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (isUniversity && subjectId != null) {
        try {
          const remote = await import('../../utils/remote');
          const res = useChapterOption ? await remote.getCourseChapterOptions(token, subjectId) : await remote.getCourseChapters(token, subjectId);
          if (!mounted) return;
          setCourseChapters(res.chapters || []);
        } catch (e) {
          
          if (mounted) setCourseChapters([]);
        }
      } else {
        if (mounted) setCourseChapters(null);
      }
    })();
    return () => { mounted = false; }
  }, [isUniversity, subjectId, token, useChapterOption]);

  // Load departments (fields) for university users
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const instId = userInfo?.institution_id ?? null;
        if (isUniversity && instId) {
          const fields = await adminGetInstitutionFields(instId);
          if (!mounted) return;
          setFieldsData(fields || []);
          // A deep-linked school grade id is not a department: fall back to the first one.
          if ((fields || []).length && !fields.some((f: AdminField) => f.id === gradeId)) setGradeId((fields[0] && fields[0].id) || null);
        } else {
          if (mounted) setFieldsData(null);
        }
      } catch (e) {
        
        if (mounted) setFieldsData([]);
      }
    };
    load();
    return () => { mounted = false; };
  }, [isUniversity, userInfo]);

  // When a department is selected, load academic years (do not auto-select a year)
  useEffect(() => {
    let mounted = true;
    const instId = userInfo?.institution_id ?? null;
    if (isUniversity && instId && gradeId) {
      (async () => {
        try {
          const years = await adminGetFieldAcademicYears(instId, gradeId);
          if (!mounted) return;
          setAcademicYears(years || []);
          // clear previously selected academic year and courses
          setAcademicYearId(null);
          setCoursesData(null);
          setSubjectId(null);
        } catch (e) {
          
          if (mounted) setAcademicYears([]);
        }
      })();
    } else {
      if (mounted) setAcademicYears(null);
    }
    return () => { mounted = false; }
  }, [isUniversity, userInfo, gradeId]);

  // When an academic year is selected, load courses for that year
  useEffect(() => {
    let mounted = true;
    const instId = userInfo?.institution_id ?? null;
    if (isUniversity && instId && gradeId && academicYearId) {
      (async () => {
        try {
          const courses = await adminGetAcademicYearCourses(instId, gradeId, academicYearId);
          if (!mounted) return;
          setCoursesData(courses || []);
          // do not auto-select a course; leave subjectId null for explicit user selection
          setSubjectId(null);
        } catch (e) {
          
          if (mounted) setCoursesData([]);
        }
      })();
    } else {
      if (mounted) setCoursesData(null);
    }
    return () => { mounted = false; }
  }, [isUniversity, userInfo, gradeId, academicYearId]);

  // Per-chapter % correct over the last 4 weeks (school flow only). It feeds the row tracks and the
  // weakest-chapters rail; both stay hidden when the request fails or returns nothing.
  const mastery = useClassPerfChapterMastery({ grade: isUniversity ? null : gradeId, subject: isUniversity || subjectId == null ? 'all' : subjectId, section: 'all', weeks: 4 });

  const doAdd = async () => {
    if (!newChapter.trim() || subjectId == null || gradeId == null) return; // Ensure gradeId is also present
    try {
      if (isUniversity) {
        const remote = await import('../../utils/remote');
        if (useChapterOption) {
          await remote.addCourseChapterOptionApi(token, subjectId, newChapter.trim());
          // refresh option chapters
          const res = await remote.getCourseChapterOptions(token, subjectId);
          setCourseChapters(res.chapters || []);
        } else {
          await remote.addCourseChapterApi(token, subjectId, newChapter.trim());
          // refresh course chapters
          const res = await remote.getCourseChapters(token, subjectId);
          setCourseChapters(res.chapters || []);
        }
      } else {
        // mutation expects a single school_grade_id (number) — remote helper will wrap it into an array
        await addChapter.mutateAsync({ toBeValidated: token, subjectId, name: newChapter.trim(), school_grade_id: gradeId });
        refetchChaps();
      }
    } catch (e) {
      
      alert('Could not add the chapter. Please try again.');
    } finally {
      setNewChapter('');
    }
  }

  const handleChapterClick = (chapterId: number, chapterName: string) => {
    setSelectedChapterId(chapterId);
    setSelectedChapterName(chapterName);
    setIsSubchapterModalOpen(true);
  };

  // Chapters of the selected grade/course that belong to this institution.
  const visibleChapters: any[] = (() => {
    const all = isUniversity ? (courseChapters || []) : (chaptersData?.chapters || []);
    const instId = userInfo?.institution_id;
    const ownedByUs = (c: any) => {
      if (role === 'superuser') return true;
      const instArr = Array.isArray(c.institutions) ? c.institutions : [];
      return !!instId && instArr.map((x: any) => Number(x)).includes(Number(instId));
    };
    if (isUniversity) return all.filter(ownedByUs);
    if (!gradeId) return [];
    return all.filter((c: any) => {
      const gradeArr = Array.isArray(c.school_grades) && c.school_grades.length ? c.school_grades : (Array.isArray(c.school_grade_ids) ? c.school_grade_ids : []);
      const inGrade = gradeArr.map((x: any) => typeof x === 'object' ? Number(x.id) : Number(x)).includes(Number(gradeId));
      return inGrade && ownedByUs(c);
    });
  })();

  const pg = usePagination(visibleChapters, 10, `${isUniversity}|${gradeId}|${subjectId}|${useChapterOption}`);
  if (!isAllowed) return <div role="alert" className="rounded-[12px] border border-danger/30 bg-danger/10 p-4 text-sm text-danger">You don't have access to chapter setup.</div>;

  const trimmedNew = newChapter.trim();
  const duplicateName = !!trimmedNew && visibleChapters.some((c: any) => String(c.name).trim().toLowerCase() === trimmedNew.toLowerCase());
  const subjectName = subsData?.subjects?.find((s: any) => s.id === subjectId)?.subject_name
    || subsData?.subjects?.find((s: any) => s.id === subjectId)?.name
    || coursesData?.find(c => c.id === subjectId)?.name || '—';
  const gradeName = isUniversity
    ? fieldsData?.find(f => f.id === gradeId)?.name
    : gradeLabel(gradesData?.grades?.find((g: any) => g.id === gradeId)?.grade_name || gradesData?.grades?.find((g: any) => g.id === gradeId)?.name) || undefined;

  const presetSelectable = presetChapters
    .filter((p: any) => !(userInfo?.institution_id && Array.isArray(p.institutions) && p.institutions.includes(userInfo.institution_id)))
    .map((p: any) => p.id);
  const allPresetsSelected = presetSelectable.length > 0 && presetSelectable.every((id: number) => selectedPresetIds.includes(id));

  const openPresets = async () => {
    if (gradeId == null || subjectId == null) return;
    try {
      const presetName = isUniversity ? 'eltest-uni' : 'eltest-school';
      const res = await (await import('../../utils/remote')).getPresetInstitutionGradeChapters(presetName, gradeId, subjectId);
      setPresetChapters(res.chapters || []);
    } catch (e) { setPresetChapters([]); }
    setSelectedPresetIds([]);
    setIsPresetModalOpen(true);
  };

  const importPresets = async () => {
    if (subjectId == null || gradeId == null) return;
    const remote = await import('../../utils/remote');
    for (const pid of selectedPresetIds) {
      const preset = presetChapters.find((c: any) => c.id === pid);
      if (!preset) continue;
      try {
        await remote.joinChapter(token, preset.id);
      } catch (joinErr) {
        try {
          await addChapter.mutateAsync({ toBeValidated: token, subjectId, name: preset.name, school_grade_id: gradeId });
        } catch (createErr) { /* reported by the refetched list */ }
      }
    }
    setIsPresetModalOpen(false);
    refetchChaps();
  };

  const submitNewChapter = async (e: FormEvent) => {
    e.preventDefault();
    if (!trimmedNew || duplicateName || subjectId == null || gradeId == null) return;
    await doAdd();
    setIsAddModalOpen(false);
  };

  const n = visibleChapters.length;
  const subCount = (c: any): number | null =>
    typeof c.subchapter_count === 'number' ? c.subchapter_count : Array.isArray(c.subchapters) ? c.subchapters.length : null;
  const subKnown = n > 0 && visibleChapters.every((c: any) => subCount(c) != null);
  const subTotal = subKnown ? visibleChapters.reduce((t: number, c: any) => t + (subCount(c) as number), 0) : 0;
  // Question counts are staff-only (null for others) and all-time; a card shows only when every row has one.
  const qKnown = n > 0 && !isUniversity && visibleChapters.every((c: any) => typeof c.question_count === 'number');
  const qTotal = qKnown ? visibleChapters.reduce((t: number, c: any) => t + c.question_count, 0) : 0;
  const withQ = qKnown ? visibleChapters.filter((c: any) => c.question_count > 0).length : 0;
  const covPct = qKnown ? Math.round((withQ / n) * 100) : 0;
  const gaps = qKnown ? visibleChapters.map((c: any, i: number) => ({ n: i + 1, name: c.name as string, q: c.question_count as number })).filter((r) => r.q === 0) : [];
  const pctByChapter = new Map<number, number | null>((mastery.data?.class_avg_per_chapter ?? []).map((r) => [r.chapter_id, r.pct]));
  const masteryOk = !isUniversity && !mastery.isError && !!mastery.data;
  const rowPct = (id: number): number | null => (masteryOk ? pctByChapter.get(id) ?? null : null);
  const weakest = masteryOk
    ? visibleChapters
        .map((c: any, i: number) => ({ n: i + 1, name: c.name as string, pct: pctByChapter.get(c.id) ?? null }))
        .filter((r) => r.pct != null)
        .sort((a, b) => (a.pct as number) - (b.pct as number))
        .slice(0, 3)
    : [];
  const showRail = !isUniversity && (weakest.length > 0 || qKnown);
  const scope = [gradeName, subjectName !== '—' ? subjectName : null].filter(Boolean).join(' · ');
  const subtitle = `${scope ? `${scope} · ` : ''}${n} ${n === 1 ? 'chapter' : 'chapters'}${subKnown && subTotal > 0 ? `, ${subTotal} ${subTotal === 1 ? 'subchapter' : 'subchapters'}` : ''}. They power exam tagging, the heatmap and the syllabus.`;
  const needsPick = gradeId == null || subjectId == null;

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        eyebrow={`Setup · Step 2 of ${setupStepTotal(role)}`}
        title="Chapters"
        subtitle={subtitle}
        actions={
          <>
            {isUniversity && (
              <>
                <input ref={fileInputRef} type="file" accept="application/json" className="hidden" onChange={handleJsonFile} />
                <Button variant="ghost" onClick={() => fileInputRef.current?.click()} disabled={subjectId == null} loading={isUploadingJson}>
                  Upload JSON
                </Button>
              </>
            )}
            <Button variant="ghost" icon={<ListFilter size={18} strokeWidth={1.8} aria-hidden />} onClick={openPresets} disabled={needsPick}>Load preset</Button>
            <Link
              to="/school-setup/class"
              className={LINK_BTN}>
              Back to classes
            </Link>
            <Button icon={<FaPlus size={18} strokeWidth={1.8} />} onClick={() => { setNewChapter(''); setIsAddModalOpen(true); }} disabled={needsPick}>
              Add chapter
            </Button>
          </>
        }
      />

      <PanelCard className="mb-[18px] !px-[18px] !py-3.5" size="compact" aria-label="Filters">
        {isUniversity ? (
          <div className="flex flex-wrap items-center gap-x-[18px] gap-y-3">
            <span className={LABEL}>Department</span>
            <Segmented
              label="Department"
              scroll
              options={(fieldsData || []).map((f: AdminField) => ({ key: String(f.id), label: f.name }))}
              value={gradeId == null ? '' : String(gradeId)}
              onChange={k => setGradeId(Number(k))}
            />
            <span aria-hidden className="h-7 w-px bg-line" />
            <span className={LABEL}>Academic year</span>
            <PillTabs
              label="Academic year"
              dense
              scroll
              options={(academicYears || []).map((y: AdminAcademicYear) => ({ key: String(y.id), label: y.name }))}
              value={academicYearId == null ? '' : String(academicYearId)}
              onChange={k => setAcademicYearId(Number(k))}
            />
            <span aria-hidden className="h-7 w-px bg-line" />
            <span className={LABEL}>Course</span>
            {(coursesData || []).length > 30 ? (
              <select id="cs-subject" aria-label="Course" className={fieldClass} value={subjectId ?? ''} onChange={e => setSubjectId(e.target.value ? Number(e.target.value) : null)}>
                <option value="">Select course</option>
                {(coursesData || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            ) : (
              <PillTabs
                label="Course"
                dense
                scroll
                options={(coursesData || []).map(c => ({ key: String(c.id), label: c.name }))}
                value={subjectId == null ? '' : String(subjectId)}
                onChange={k => setSubjectId(Number(k))}
              />
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-x-[18px] gap-y-3">
            <span className={LABEL}>Grade</span>
            <Segmented
              label="Grade"
              scroll
              options={(gradesData?.grades ?? []).map((g: any) => ({ key: String(g.id), label: gradeCompact(g.grade_name || g.name), title: g.grade_name || g.name }))}
              value={gradeId == null ? '' : String(gradeId)}
              onChange={k => { setGradeId(Number(k)); setSubjectId(null); }}
            />
            <span aria-hidden className="h-7 w-px bg-line" />
            <span className={LABEL}>Subject</span>
            <div className="min-w-0 flex-1">
              <PillTabs
                label="Subject"
                dense
                scroll
                options={(subsData?.subjects ?? []).map((s: any) => ({ key: String(s.id), label: String(s.subject_name || s.name) }))}
                value={subjectId == null ? '' : String(subjectId)}
                onChange={k => setSubjectId(Number(k))}
              />
            </div>
          </div>
        )}
      </PanelCard>

      <div className="mb-[18px] grid grid-cols-1 gap-[18px] sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard label="Chapters" value={n} sub={isUniversity ? 'In this course' : 'In this grade and subject'} />
        {subKnown && <KpiCard label="Subchapters" value={subTotal} sub="Used for fine-grained tagging" />}
        {qKnown && <KpiCard label="Tagged questions" value={qTotal} sub="Across all exams" />}
        {qKnown && (
          <KpiCard
            label="Exam coverage"
            value={covPct}
            unit="%"
            size="lg"
            track={<Track value={covPct} tone={covPct < 60 ? 'accent' : 'slate'} label="Chapters with at least one question" />}
            sub={`${withQ} of ${n} chapters have questions`}
          />
        )}
      </div>

      <div className={`grid grid-cols-1 gap-[18px] ${showRail ? 'lg:grid-cols-[minmax(0,8fr)_minmax(0,4fr)]' : ''}`}>
        <PanelCard
          aria-label="Chapter list"
          title={`${subjectName} · ${gradeName ?? '—'}`}
          subtitle={isUniversity ? undefined : 'In teaching order · % correct from the last 4 weeks'}
          actions={
            <div className="flex items-center gap-2.5 text-[13px] font-medium text-ink">
              <Switch id="cs-use-options" tone="navy" label="Use chapter options" checked={useChapterOption} onChange={setUseChapterOption} />
              <label htmlFor="cs-use-options" className="cursor-pointer">Use chapter options</label>
            </div>
          }
        >
          {isFetching ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : visibleChapters.length ? (
            <>
              <ul>
                {pg.pageItems.map((c: any, i: number) => {
                  const num = pg.from + i;
                  const open = expandedChapterId === c.id;
                  const pct = rowPct(c.id);
                  const sc = subCount(c);
                  const metaParts = [
                    sc != null ? `${sc} ${sc === 1 ? 'subchapter' : 'subchapters'}` : null,
                    typeof c.question_count === 'number' ? `${c.question_count} questions tagged` : null,
                  ].filter(Boolean);
                  return (
                    <li key={c.id} className="border-b border-hairline last:border-b-0">
                      <div className="flex flex-wrap items-center gap-3.5 px-1 py-3.5 hover:bg-surface-2">
                        <button
                          type="button"
                          aria-label={`${open ? 'Hide' : 'Show'} subchapters of ${c.name}`}
                          aria-expanded={open}
                          onClick={() => { setInlineSub(''); setInlineSubError(null); setExpandedChapterId(open ? null : c.id); }}
                          className="grid h-9 w-9 flex-none place-items-center rounded-[10px] border border-line-strong bg-surface text-muted-2 hover:border-slate focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                        >
                          <FaChevronRight size={18} strokeWidth={1.8} className={`transition-transform ${open ? 'rotate-90' : ''}`} />
                        </button>
                        <span aria-hidden className="grid h-11 w-11 flex-none place-items-center rounded-[13px] bg-mist font-mono text-[13px] font-semibold text-navy dark:text-ink">U{num}</span>
                        <span className="min-w-[200px] flex-1">
                          <span className="block break-words font-semibold text-ink">{c.name}</span>
                          {metaParts.length > 0 && <span className="text-xs text-muted">{metaParts.join(' · ')}</span>}
                        </span>
                        <span className="flex flex-none items-center gap-4 max-sm:w-full max-sm:justify-between">
                          {masteryOk && (
                            <span className="flex w-[140px] items-center gap-2">
                              <Track className="flex-1" value={pct ?? 0} tone={pct != null && pct < 50 ? 'accent' : 'slate'} label={`${c.name} % correct`} />
                              <span className="w-9 text-right font-mono text-[13px] font-medium text-ink">{pct == null ? '—' : `${Math.round(pct)}%`}</span>
                            </span>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => handleChapterClick(c.id, c.name)}>Subchapters</Button>
                          {!isUniversity && (
                            <Button size="sm" variant="ghost" aria-label={`Remove ${c.name}`} onClick={() => setRemoveTarget({ kind: 'chapter', chapterId: c.id, name: c.name })}>Remove</Button>
                          )}
                        </span>
                      </div>
                      {open && (
                        <div className="mb-3.5 ml-[62px] mr-1 flex flex-col gap-0.5 rounded-[14px] bg-panel px-3.5 py-3 max-sm:ml-1">
                          {Array.isArray(c.subchapters) && c.subchapters.length > 0 ? (
                            c.subchapters.map((sc2: any, k: number) => (
                              <div key={sc2.id} className="flex items-center gap-2.5 border-b border-dashed border-line-strong py-[7px] text-[13px] last:border-b-0">
                                <span className="w-[34px] font-mono text-[11px] font-semibold text-muted">{num}.{k + 1}</span>
                                <span className="min-w-0 flex-1 break-words text-ink">{sc2.name}</span>
                                {typeof sc2.question_count === 'number' && <span className="font-mono text-xs text-muted">{sc2.question_count} q</span>}
                              </div>
                            ))
                          ) : Array.isArray(c.subchapters) ? (
                            <p className="py-1 text-[13px] text-muted">No subchapters yet.</p>
                          ) : null}
                          <form
                            className="mt-2 flex min-h-[40px] items-center gap-2 rounded-[12px] border border-line-strong bg-surface pl-3.5 pr-1.5"
                            onSubmit={async (e) => {
                              e.preventDefault();
                              const nm = inlineSub.trim();
                              if (!nm) return;
                              const dupe = (c.subchapters || []).some((x: any) => String(x.name).trim().toLowerCase() === nm.toLowerCase());
                              if (dupe) { setInlineSubError('A subchapter with this name already exists here.'); return; }
                              setInlineSubError(null);
                              try {
                                await addSubchapter.mutateAsync({ toBeValidated: token, chapterId: c.id, name: nm });
                                setInlineSub('');
                                if (isUniversity && subjectId != null) {
                                  // University lists come from course-chapter state, not the school hook.
                                  const remote = await import('../../utils/remote');
                                  const res = useChapterOption ? await remote.getCourseChapterOptions(token, subjectId) : await remote.getCourseChapters(token, subjectId);
                                  setCourseChapters(res.chapters || []);
                                } else {
                                  refetchChaps();
                                }
                              } catch (err) {
                                setInlineSubError('Could not add the subchapter. Try again.');
                              }
                            }}
                          >
                            <input
                              aria-label={`New subchapter name for ${c.name}`}
                              value={inlineSub}
                              maxLength={NAME_MAX}
                              onChange={(e) => setInlineSub(e.target.value)}
                              placeholder="New subchapter name"
                              className="min-w-0 flex-1 border-0 bg-transparent text-sm text-ink outline-none placeholder:text-muted"
                            />
                            <Button type="submit" variant="secondary" size="sm" loading={addSubchapter.isPending} disabled={!inlineSub.trim()}>Add</Button>
                          </form>
                          {inlineSubError && <p role="alert" className="mt-1.5 text-xs text-danger">{inlineSubError}</p>}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
              <PagedFooter from={pg.from} to={pg.to} total={pg.total} page={pg.page} pageCount={pg.pageCount} onPage={pg.setPage} className="pt-4" />
            </>
          ) : (
            <EmptyState
              size="lg"
              icon={<FaBookOpen size={18} strokeWidth={1.8} />}
              title="No chapters yet"
              body={subjectId == null ? 'Pick a subject above to see its chapters.' : `Add the first chapter, or load the standard chapters for ${[gradeName, subjectName !== '—' ? subjectName : null].filter(Boolean).join(' ')} from a preset.`}
              action={
                <>
                  <Button onClick={openPresets} disabled={needsPick}>Load preset chapters</Button>
                  <Button variant="ghost" onClick={() => { setNewChapter(''); setIsAddModalOpen(true); }} disabled={needsPick}>Add chapter</Button>
                </>
              }
            />
          )}
        </PanelCard>

        {showRail && (
          <div className="flex min-w-0 flex-col gap-[18px]">
            {qKnown && (
              <section aria-label="Coverage" className="relative flex min-w-0 flex-col gap-4 overflow-hidden rounded-[22px] bg-navy p-6 text-white">
                <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-on-navy-muted">Needs questions</div>
                <div className="flex items-baseline gap-2.5">
                  <span className="font-mono text-[46px] font-semibold leading-none tracking-[-0.04em]">{gaps.length}</span>
                  <span className="font-display text-[17px] font-bold">{gaps.length === 1 ? 'chapter' : 'chapters'} with no exam yet</span>
                </div>
                {gaps.length > 0 && (
                  <ul className="flex flex-col gap-2">
                    {gaps.slice(0, 5).map((g) => (
                      <li key={g.n} className="flex items-center gap-3 rounded-[14px] bg-navy-2 p-3">
                        <span aria-hidden className="grid h-[38px] w-[38px] flex-none place-items-center rounded-[12px] bg-mist-fixed text-xs font-semibold text-navy">U{g.n}</span>
                        <span className="min-w-0 flex-1 break-words font-semibold">{g.name}</span>
                      </li>
                    ))}
                    {gaps.length > 5 && <li className="px-1 text-xs text-on-navy-muted">+ {gaps.length - 5} more</li>}
                  </ul>
                )}
                <Link
                  to="/exams/generate"
                  className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-[12px] border border-accent bg-accent px-4 text-sm font-semibold text-navy hover:brightness-105"
                >
                  Generate a chapter quiz
                  <ArrowRight size={18} strokeWidth={1.8} aria-hidden />
                </Link>
              </section>
            )}
            {weakest.length > 0 && (
              <PanelCard title="Weakest chapters" actions={<span className="text-[13px] text-muted">% correct</span>}>
                <ul>
                  {weakest.map((w) => (
                    <li key={w.n} className="flex items-center gap-3 border-b border-hairline py-[11px] last:border-b-0">
                      <span className="min-w-0 flex-1">
                        <span className="block break-words font-semibold text-ink">{w.name}</span>
                        <span className="text-xs text-muted">Unit {w.n}</span>
                      </span>
                      <span className="font-mono font-semibold text-ink">{Math.round(w.pct as number)}%</span>
                    </li>
                  ))}
                </ul>
                <TextLink to="/class-performance" className="mt-2">Open chapter heatmap →</TextLink>
              </PanelCard>
            )}
          </div>
        )}
      </div>

      <SubchapterDialog
        isOpen={isSubchapterModalOpen}
        onClose={() => setIsSubchapterModalOpen(false)}
        chapterId={selectedChapterId}
        chapterName={selectedChapterName}
      />

      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add chapter"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsAddModalOpen(false)}>Cancel</Button>
            <Button type="submit" form="add-chapter-form" loading={addChapter.isPending} disabled={!trimmedNew || duplicateName}>Save chapter</Button>
          </>
        }
      >
        <form id="add-chapter-form" onSubmit={submitNewChapter} className="space-y-4">
          <p className="rounded-[12px] bg-surface-2 px-3 py-2 text-sm text-muted">
            Adding to <span className="font-medium text-ink">{gradeName ? `${gradeName} · ` : ''}{subjectName}</span>
          </p>
          <FormField
            label="Chapter name"
            htmlFor="new-chapter"
            required
            error={duplicateName ? 'A chapter with this name already exists here.' : undefined}
            hint={`${trimmedNew.length}/${NAME_MAX} characters`}
          >
            <input
              id="new-chapter"
              className={fieldClass}
              value={newChapter}
              maxLength={NAME_MAX}
              onChange={e => setNewChapter(e.target.value)}
              placeholder="e.g. Unit 1: Linear equations"
              autoFocus
            />
          </FormField>
        </form>
      </Modal>

      <Modal
        isOpen={isPresetModalOpen}
        onClose={() => setIsPresetModalOpen(false)}
        title="Load preset chapters"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsPresetModalOpen(false)}>Cancel</Button>
            <Button onClick={importPresets} loading={addChapter.isPending} disabled={selectedPresetIds.length === 0}>
              Import {selectedPresetIds.length ? `${selectedPresetIds.length} ` : ''}selected
            </Button>
          </>
        }
      >
        {presetChapters.length > 0 ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted">Pick the chapters to add.</p>
              <button
                type="button"
                className="text-sm font-semibold text-slate hover:underline disabled:opacity-50"
                disabled={presetSelectable.length === 0}
                onClick={() => setSelectedPresetIds(s => allPresetsSelected
                  ? s.filter(id => !presetSelectable.includes(id))
                  : Array.from(new Set([...s, ...presetSelectable])))}
              >
                {allPresetsSelected ? 'Clear selection' : 'Select all'}
              </button>
            </div>
            <ul className="max-h-72 space-y-2 overflow-y-auto pr-1">
              {presetChapters.map((p: any) => {
                const already = !!(userInfo?.institution_id && Array.isArray(p.institutions) && p.institutions.includes(userInfo.institution_id));
                const checked = selectedPresetIds.includes(p.id);
                return (
                  <li key={p.id}>
                    <label className={`flex items-center gap-3 rounded-[12px] border px-3 py-2.5 text-sm ${already ? 'cursor-not-allowed border-line opacity-60' : checked ? 'cursor-pointer border-navy bg-surface-2' : 'cursor-pointer border-line hover:border-line-strong'}`}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-navy"
                        checked={checked}
                        disabled={already}
                        onChange={e => setSelectedPresetIds(s => e.target.checked ? [...s, p.id] : s.filter(id => id !== p.id))}
                      />
                      <span className="min-w-0 flex-1 break-words text-ink">{p.name}</span>
                      {already && <span className="text-xs text-muted">Already added</span>}
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <EmptyState compact icon={<FaBookOpen size={18} strokeWidth={1.8} />} title="No preset chapters" body="There's no standard chapter list for this grade and subject yet." />
        )}
      </Modal>
      <RemoveDialog target={removeTarget} onClose={() => setRemoveTarget(null)} onRemoved={() => refetchChaps()} />
    </div>
  );
};

export default ChapterSetup;
